import _ from "lodash";
import React from "react";
import { RadioButton } from "@toddle-edu/ds-web";
import { generateRandomId } from "Utils";
import update from "immutability-helper";
import {
  SUBJECT_TYPE_TOK,
  SUBJECT_TYPE_OTHERS,
  NOVEMBER,
  CORE_TOK_AND_OTHER_TYPES,
  FINAL_SCORE,
} from "Constants/stringConstants";
import { getIsCategorySetsExistsForAllSubjects } from "ScoreCategories/modules/Utils";
import { getTermWiseCategorySetsCount } from "../../AssessmentCategories/modules/AssessmentCategoriesModule";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";

// useReducer params
export const initialState = {
  gradeBoundaries: [
    {
      gradeId: generateRandomId(),
      gradeLabel: "",
      mode: "view",
      fieldValues: [
        {
          id: null,
          label: "",
          SL: {
            minScore: "",
            maxScore: "",
          },
          HL: {
            minScore: "",
            maxScore: "",
          },
        },
        {
          id: null,
          label: "",
          SL: {
            minScore: "",
            maxScore: "",
          },
          HL: {
            minScore: "",
            maxScore: "",
          },
        },
      ],
    },
  ],
};

export const finalGradeTableReducer = (state, action) => {
  const payload = _.get(action, "payload", {});
  switch (action?.type) {
    case "set":
      return { gradeBoundaries: payload };
    case "setById": {
      const { gradeId, gradeBoundary } = payload;

      const gradeBoundaries = _.map(state?.gradeBoundaries, boundary => {
        const boundaryGradeId = _.get(boundary, "gradeId");

        if (boundaryGradeId === gradeId) {
          return gradeBoundary;
        } else return boundary;
      });

      return { gradeBoundaries };
    }
    case "updateMode": {
      const { mode } = payload;
      const gradeBoundaries = _.map(state?.gradeBoundaries, boundary => {
        return { ...boundary, mode };
      });

      return { gradeBoundaries };
    }
    case "updateModeById": {
      const { gradeId, mode } = payload;
      const gradeBoundaries = _.map(state?.gradeBoundaries, boundary => {
        const boundaryGradeId = _.get(boundary, "gradeId");

        if (boundaryGradeId === gradeId) {
          return {
            ...boundary,
            mode,
          };
        } else return boundary;
      });

      return { gradeBoundaries };
    }
    case "update": {
      const {
        gradeId,
        genericTag,
        minScore = "",
        maxScore = "",
        finalGradeId,
        isMinScore,
      } = payload || {};

      const scoreLabel = isMinScore ? "minScore" : "maxScore";
      const score = isMinScore ? minScore : maxScore;

      const gradeBoundaries = _.map(state.gradeBoundaries, boundary => {
        const boundaryGradeId = _.get(boundary, "gradeId");

        if (boundaryGradeId === gradeId) {
          const fieldValues = _.get(boundary, "fieldValues", []);

          const updatedFieldValues = _.map(fieldValues, value => {
            if (value?.id === finalGradeId) {
              return update(value, {
                [`${genericTag}`]: {
                  [`${scoreLabel}`]: {
                    $set: score,
                  },
                },
              });
            } else {
              return value;
            }
          });

          return {
            ...boundary,
            fieldValues: updatedFieldValues,
          };
        } else return boundary;
      });

      return { gradeBoundaries };
    }
    default:
      return state;
  }
};

// Utils

export const getFinalGrades = ({ subjectType }) => {
  const finalGrades = [7, 6, 5, 4, 3, 2, 1];
  const finalGradesTOK = ["A", "B", "C", "D", "E"];

  const isTOK = subjectType === SUBJECT_TYPE_TOK;
  const _finalGrades = isTOK ? finalGradesTOK : finalGrades;

  return _.map(_finalGrades, (grade, index) => {
    const isFirstGrade = index === 0;
    const isLastGrade = index === _.size(_finalGrades) - 1;

    return {
      id: generateRandomId(),
      finalGrade: grade,
      SL: {
        minScore: isLastGrade ? "0" : "",
        maxScore: isFirstGrade ? (isTOK ? "30" : "100") : "",
      },
      HL: {
        minScore: isLastGrade ? "0" : "",
        maxScore: isFirstGrade ? (isTOK ? "30" : "100") : "",
      },
    };
  });
};

const _getCategories = ({
  gradeId,
  gradeBoundaries,
  subjectLevelGenericTags,
  weightageType,
}) => {
  const currentGrade = _.find(gradeBoundaries, boundary => {
    const boundaryGradeId = _.get(boundary, "gradeId", "");
    return boundaryGradeId === gradeId;
  });

  const fieldValues = _.get(currentGrade, "fieldValues", []);

  return _.map(fieldValues, (value, index) => {
    const finalGradeLabel = _.get(value, "finalGrade", "");

    const genericTagIdSL = _.find(subjectLevelGenericTags, { label: "SL" })?.id;
    const genericTagIdHL = _.find(subjectLevelGenericTags, { label: "HL" })?.id;

    const values = [];

    const hasPrevRow = index > 0;
    const prevRowFieldvalue = hasPrevRow ? fieldValues[index - 1] : {};

    if (genericTagIdSL) {
      const scoreSL = _.get(value, "SL", {});
      const { minScore: minScoreSL, maxScore: maxScoreSL } = scoreSL;

      const prevRowScoreSL = hasPrevRow
        ? _.get(prevRowFieldvalue, "SL", {})
        : {};
      const { minScore: prevRowMinScoreSL } = prevRowScoreSL;

      // assign maxValue as the minValue of prevRow if prevRow exists
      values.push({
        minValue: parseFloat(minScoreSL),
        maxValue: hasPrevRow
          ? parseFloat(prevRowMinScoreSL)
          : parseFloat(maxScoreSL),
        genericTagId: genericTagIdSL,
        valueType: "RANGE",
        weightageType,
      });
    }

    if (genericTagIdHL) {
      const scoreHL = _.get(value, "HL", {});
      const { minScore: minScoreHL, maxScore: maxScoreHL } = scoreHL;

      const prevRowScoreHL = hasPrevRow
        ? _.get(prevRowFieldvalue, "HL", {})
        : {};
      const { minScore: prevRowMinScoreHL } = prevRowScoreHL;

      // assign maxValue as the minValue of prevRow if prevRow exists
      values.push({
        minValue: parseFloat(minScoreHL),
        maxValue: hasPrevRow
          ? parseFloat(prevRowMinScoreHL)
          : parseFloat(maxScoreHL),
        genericTagId: genericTagIdHL,
        valueType: "RANGE",
        weightageType,
      });
    }

    // for core classes

    if (!genericTagIdHL && !genericTagIdSL) {
      const scoreHL = _.get(value, "HL", {});
      const { minScore: minScoreHL, maxScore: maxScoreHL } = scoreHL;

      const prevRowScoreHL = hasPrevRow
        ? _.get(prevRowFieldvalue, "HL", {})
        : {};
      const { minScore: prevRowMinScoreHL } = prevRowScoreHL;

      values.push({
        minValue: parseFloat(minScoreHL),
        maxValue: hasPrevRow
          ? parseFloat(prevRowMinScoreHL)
          : parseFloat(maxScoreHL),
        valueType: "RANGE",
        weightageType,
      });
    }

    return {
      label: _.toString(finalGradeLabel),
      values,
    };
  });
};

const _getCurrentGradeFromGradeScale = ({
  organizationGradeScaleSet,
  gradeId,
}) => {
  return _.find(organizationGradeScaleSet, ({ node }) => {
    const associatedEntities = _.get(node, "associatedEntities", []);

    const grade = _.find(associatedEntities, entity => {
      return _.get(entity, "__typename") === "Grade";
    });

    return grade?.id === gradeId;
  });
};

const _getGradeBoundariesStructure = ({
  grades,
  organizationGradeScaleSet,
  includeScoreValues = false,
  subjectLevelGenericTags = [],
  subjectType,
}) => {
  const isTOK = subjectType === SUBJECT_TYPE_TOK;
  return _.map(grades, grade => {
    const gradeName = _.get(grade, "name", "");
    const gradeId = _.get(grade, "id", "");

    const currentGrade = getCurrentGradeFromGradeScale({
      organizationGradeScaleSet,
      gradeId,
    });

    if (currentGrade) {
      const id = _.get(currentGrade, "node.id");
      const label = _.get(currentGrade, "node.label");
      const subType = _.get(currentGrade, "node.subType");
      const metadata = _.get(currentGrade, "node.metaData", {});

      const categories = _.orderBy(
        _.get(currentGrade, "node.categories", []),
        "label",
        isTOK ? "asc" : "desc"
      );

      const _finalGrades = _.map(categories, (category, index) => {
        const values = _.get(category, "values", []);

        let finalGradesObj = {
          id: _.get(category, "id"),
          finalGrade: _.get(category, "label"),
        };

        if (!_.isEmpty(subjectLevelGenericTags)) {
          _.forEach(subjectLevelGenericTags, tag => {
            const tagId = _.get(tag, "id");
            const tagLabel = _.get(tag, "label");

            const value = _.find(values, value => {
              return _.get(value, "genericTag.id") === tagId;
            });

            if (!_.isEmpty(value)) {
              if (tagLabel === "SL") {
                finalGradesObj = {
                  ...finalGradesObj,
                  SL: {
                    minScore: _.get(value, "minValue"),
                    maxScore: _.get(value, "maxValue"),
                    ...(includeScoreValues && {
                      genericTag: _.get(value, "genericTag", {}),
                      categoryValueId: _.get(value, "id", ""),
                    }),
                  },
                };
              }

              if (tagLabel === "HL") {
                finalGradesObj = {
                  ...finalGradesObj,
                  HL: {
                    minScore: _.get(value, "minValue"),
                    maxScore: _.get(value, "maxValue"),
                    ...(includeScoreValues && {
                      genericTag: _.get(value, "genericTag", {}),
                      categoryValueId: _.get(value, "id", ""),
                    }),
                  },
                };
              }
            } else {
              finalGradesObj = {
                ...finalGradesObj,
                [`${tagLabel}`]: {
                  minScore: _.size(categories) - 1 === index ? "0" : "",
                  maxScore: index == 0 ? "100" : "",
                },
              };
            }
          });
        } else {
          _.forEach(values, value => {
            const tag = _.get(value, "genericTag.label");
            if (_.isEmpty(tag)) {
              finalGradesObj = {
                ...finalGradesObj,
                HL: {
                  minScore: _.get(value, "minValue"),
                  maxScore: _.get(value, "maxValue"),
                  ...(includeScoreValues && {
                    categoryValueId: _.get(value, "id", ""),
                  }),
                },
              };
            }
          });
        }

        return finalGradesObj;
      });

      return {
        id,
        label,
        subType,
        metadata,
        gradeId,
        gradeLabel: gradeName,
        fieldValues: _finalGrades,
        mode: "view",
      };
    } else {
      return {
        gradeId,
        gradeLabel: gradeName,
        fieldValues: getFinalGrades({ subjectType }),
        mode: "view",
      };
    }
  });
};

const _getUpdatedCategorieValues = ({
  prevResult,
  currentResult,
  weightageType,
  subjectLevelGenericTags,
}) => {
  const currentQueryFieldsKeyBy = _.keyBy(
    _.get(prevResult, "fieldValues", []),
    "id"
  );

  const fieldValues = _.get(currentResult, "fieldValues", []);

  const updatedCategories = _.map(fieldValues, value => {
    const grade = _.get(value, "finalGrade", "");

    const updatedCategorieValues = [];
    const addedCategorieValues = [];

    _.forEach(
      !_.isEmpty(subjectLevelGenericTags)
        ? subjectLevelGenericTags
        : [{ label: "HL" }],
      tag => {
        const tagLabel = _.get(tag, "label", "");
        const scoreRange = _.get(value, tagLabel, {});
        const scoreRangeFromQuery = _.get(
          currentQueryFieldsKeyBy?.[value?.id],
          tagLabel,
          {}
        );

        const id = _.get(scoreRangeFromQuery, "categoryValueId");

        if (!_.isEmpty(id)) {
          updatedCategorieValues.push({
            id,
            minValue: _.toNumber(_.get(scoreRange, "minScore", "")),
            maxValue: _.toNumber(_.get(scoreRange, "maxScore", "")),
            genericTagId: _.get(tag, "id", null),
            valueType: "RANGE",
            weightageType,
          });
        } else {
          addedCategorieValues.push({
            minValue: _.toNumber(_.get(scoreRange, "minScore", "")),
            maxValue: _.toNumber(_.get(scoreRange, "maxScore", "")),
            genericTagId: _.get(tag, "id", null),
            valueType: "RANGE",
            weightageType,
          });
        }
      }
    );

    return {
      id: _.get(value, "id"),
      label: _.toString(grade),
      values: {
        updatedCategorieValues,
        addedCategorieValues,
      },
    };
  });

  return { updatedCategories };
};

export const _getSubjectNameAndGenericTags = ({
  subjects,
  subjectId,
  isSubjectSettingsContext = false,
}) => {
  const currentSubject = _.find(
    subjects,
    subject => _.get(subject, "id") === subjectId
  );
  const subjectName = _.get(currentSubject, "name", "");
  const flatVariants = _.get(currentSubject, "flatVariants", []);
  const variants = _.get(currentSubject, "variants", []);
  const subjectLevelGenericTags = _.orderBy(
    _.filter(
      _.uniqBy(
        _.map(isSubjectSettingsContext ? variants : flatVariants, item =>
          _.get(item, "level")
        ),
        "id"
      ),
      tag => _.includes(["SL", "HL"], _.get(tag, "label", ""))
    ),
    "label",
    "desc"
  );

  return {
    subjectName,
    subjectLevelGenericTags,
    subjectType: _.get(currentSubject, "type"),
    isArchived: _.get(currentSubject, "isArchived", false),
  };
};

const _getHeaderLabels = ({
  t,
  hasSL,
  hasHL,
  noSLorHL,
  isTOK,
  criteriaLabels,
}) => {
  const headerItems = [];

  const finalScoreLabel = _.get(criteriaLabels, FINAL_SCORE, null);

  if (hasSL) {
    const slTag = t("common:SL");
    const percentageLabel = !_.isNil(finalScoreLabel)
      ? `${finalScoreLabel} (${slTag})`
      : t("academicSetup:weightage_percentage_score_with_label", {
          label: slTag,
        });

    const absoluteLabel = t("academicSetup:absolute_weight_with_label", {
      label: slTag,
    });

    headerItems.push({
      percentageLabel,
      absoluteLabel,
      key: "SCORE_SL",
      maxValue: 100,
    });
  }

  if (hasHL) {
    const hlTag = t("common:HL");
    const percentageLabel = !_.isNil(finalScoreLabel)
      ? `${finalScoreLabel} (${hlTag})`
      : t("academicSetup:weightage_percentage_score_with_label", {
          label: hlTag,
        });

    const absoluteLabel = t("academicSetup:absolute_weight_with_label", {
      label: hlTag,
    });

    headerItems.push({
      percentageLabel,
      absoluteLabel,
      key: "SCORE_HL",
      maxValue: 100,
    });
  }

  if (noSLorHL && !isTOK) {
    const percentageLabel = _.get(
      criteriaLabels,
      FINAL_SCORE,
      t("academicSetup:weighted_percentage_score")
    );
    const absoluteLabel = t("academicSetup:absolute_weights");
    headerItems.push({
      percentageLabel,
      absoluteLabel,
      key: "SCORE_WITHOUT_HL_OR_SL",
      maxValue: 100,
    });
  }

  if (isTOK) {
    const finalScoreLabel = _.get(
      criteriaLabels,
      FINAL_SCORE,
      t("common:final_score")
    );
    const equivalentLabel = t("common:equivalent");

    headerItems.push(
      {
        percentageLabel: finalScoreLabel,
        absoluteLabel: finalScoreLabel,
        key: "SCORE_HL",
        maxValue: 30,
      },
      {
        percentageLabel: equivalentLabel,
        absoluteLabel: equivalentLabel,
        key: "EQUIVALENT",
        dependentedKey: "SCORE_HL",
        maxValue: 100,
      }
    );
  }

  return headerItems;
};

const _checkAreScoresValid = ({
  t,
  boundary,
  gradeId,
  hasSL,
  hasHL,
  noSLorHL,
}) => {
  let errors = {};

  const fieldValues = _.get(boundary, "fieldValues", []);

  for (let i = 0; i < _.size(fieldValues); i++) {
    const { SL: currentSL = {}, HL: currentHL = {}, id } = fieldValues[i] || {};
    const { SL: nextSL = {}, HL: nextHL = {} } = fieldValues[i + 1] || {};

    const isFirstRow = i == 0;

    if (fieldValues[i + 1]) {
      const {
        minScore: _currentMinScoreSL,
        maxScore: _currentMaxScoreSL,
      } = currentSL;
      const { minScore: _nextMinScoreSL } = nextSL;

      const currentMaxScoreSL = parseFloat(_currentMaxScoreSL);
      const currentMinScoreSL = parseFloat(_currentMinScoreSL);
      const nextMinScoreSL = parseFloat(_nextMinScoreSL);

      // SL Errors

      if (hasSL) {
        if (nextMinScoreSL >= currentMinScoreSL) {
          errors = {
            ...errors,
            [`${id}_SL_minScore`]: {
              errorMsg: t("academicSetup:min_score_must_be_higher_than_label", {
                label: nextMinScoreSL,
              }),
            },
          };
        }
        if (isFirstRow && currentMinScoreSL > currentMaxScoreSL) {
          errors = {
            ...errors,
            [`${id}_SL_minScore`]: {
              errorMsg: t(
                "academicSetup:min_score_higher_than_max_score_label",
                {
                  label: currentMaxScoreSL,
                }
              ),
            },
          };
        }

        if (_.isEmpty(_.toString(_currentMinScoreSL))) {
          errors = {
            ...errors,
            [`${id}_SL_minScore`]: {
              errorMsg: t("academicSetup:this_field_is_compulsory"),
            },
          };
        }
      }

      if (hasHL || noSLorHL) {
        const {
          minScore: _currentMinScoreHL,
          maxScore: _currentMaxScoreHL,
        } = currentHL;
        const { minScore: _nextMinScoreHL } = nextHL;

        const currentMinScoreHL = parseFloat(_currentMinScoreHL);
        const currentMaxScoreHL = parseFloat(_currentMaxScoreHL);
        const nextMinScoreHL = parseFloat(_nextMinScoreHL);

        if (nextMinScoreHL >= currentMinScoreHL) {
          errors = {
            ...errors,
            [`${id}_HL_minScore`]: {
              errorMsg: t("academicSetup:min_score_must_be_higher_than_label", {
                label: nextMinScoreHL,
              }),
            },
          };
        }

        if (isFirstRow && currentMinScoreHL > currentMaxScoreHL) {
          errors = {
            ...errors,
            [`${id}_HL_minScore`]: {
              errorMsg: t(
                "academicSetup:min_score_higher_than_max_score_label",
                {
                  label: currentMaxScoreHL,
                }
              ),
            },
          };
        }

        if (_.isEmpty(_.toString(_currentMinScoreHL))) {
          errors = {
            ...errors,
            [`${id}_HL_minScore`]: {
              errorMsg: t("academicSetup:this_field_is_compulsory"),
            },
          };
        }
      }
    }
  }

  return {
    [`${gradeId}`]: errors,
  };
};

export const getSubjectNameAndGenericTags = _.memoize(
  params => _getSubjectNameAndGenericTags(params),
  params => JSON.stringify(params)
);

export const getUpdatedCategorieValues = _.memoize(
  params => _getUpdatedCategorieValues(params),
  params => JSON.stringify(params)
);

export const getGradeBoundariesStructure = _.memoize(
  params => _getGradeBoundariesStructure(params),
  params => JSON.stringify(params)
);

export const getCategories = _.memoize(
  params => _getCategories(params),
  params => JSON.stringify(params)
);

export const getCurrentGradeFromGradeScale = _.memoize(
  params => _getCurrentGradeFromGradeScale(params),
  params => JSON.stringify(params)
);

export const getHeaderLabels = _.memoize(
  params => _getHeaderLabels(params),
  params => JSON.stringify(params)
);

export const checkAreScoresValid = _.memoize(
  params => _checkAreScoresValid(params),
  params => JSON.stringify(params)
);

export const getDropdownOptions = _.memoize(
  ({
    t,
    otherGradeTemplate,
    gradeId,
    organizationGradeScaleSet,
    doesAllSubjectsHaveTemplates,
    doesAllTermsHaveGradeBoundaries,
    isGradeBoundaryEmpty,
    sourceFrom = "",
    rootNodes,
    subjectId,
    examSessionMonth,
    examSessionTimezone,
    isCurriculumDP,
    isCurriculumMYP,
    isGradingPeriodUnlocked,
  }) => {
    const currentGradeBoundarySet = getCurrentGradeFromGradeScale({
      organizationGradeScaleSet,
      gradeId,
    });

    const currentSubjectType = _.get(
      _.find(rootNodes, rootNode => rootNode?.id === subjectId),
      "type"
    );

    const isGradeBoundaryDefinedForCurrentGrade = !_.isEmpty(
      currentGradeBoundarySet
    );

    const showResetToIBDefaultOption =
      (examSessionMonth === NOVEMBER || !_.isEmpty(examSessionTimezone)) &&
      currentSubjectType !== SUBJECT_TYPE_OTHERS &&
      isCurriculumDP;

    const resetToIBDefaultOption = showResetToIBDefaultOption
      ? [
          {
            key: "RESET_TO_IB_DEFAULT",
            label: t("academicSetup:use_the_ib_defined_grade_boundary"),
            disabled: !isGradingPeriodUnlocked,
            disabledTooltip: !isGradingPeriodUnlocked
              ? t(
                  "academicSetup:grading_period_is_locked_grading_configurations_cannot_be_edited"
                )
              : null,
          },
        ]
      : [];

    const duplicateOptions = [];
    if (
      !_.isEmpty(otherGradeTemplate) &&
      !_.isEqual(sourceFrom, "CLASS_SETTINGS")
    ) {
      duplicateOptions.push({
        key: "DUPLICATE_TO_OTHER_GRADE",
        label: t("academicSetup:duplicate_to_label", {
          label: _.get(otherGradeTemplate, "gradeLabel"),
        }),
        disabled: !isGradeBoundaryDefinedForCurrentGrade,
        visible: !isCurriculumMYP,
      });
    }
    if (
      !_.includes(CORE_TOK_AND_OTHER_TYPES, currentSubjectType) &&
      !_.isEqual(sourceFrom, "CLASS_SETTINGS")
    ) {
      duplicateOptions.push({
        key: "BULK_DUPLICATE_TO_OTHER_SUBJECTS",
        label: isCurriculumMYP
          ? t("academicSetup:duplicate_to_other_subjects_and_grade_levels")
          : t("academicSetup:duplicate_to_other_subjects"),
        disabled:
          !isGradeBoundaryDefinedForCurrentGrade ||
          doesAllSubjectsHaveTemplates ||
          !isGradingPeriodUnlocked,
        disabledTooltip: doesAllSubjectsHaveTemplates
          ? t("academicSetup:grade_boundaries_already_exist_for_all_subjects")
          : null,
      });
    }

    if (_.isEqual(sourceFrom, "CLASS_SETTINGS")) {
      duplicateOptions.push({
        key: "DUPLICATE_TO_OTHER_TERMS",
        label: t("academicSetup:duplicate_to_other_terms"),
        disabledTooltip: doesAllTermsHaveGradeBoundaries
          ? t("academicSetup:grade_boundaries_already_created_for_all_terms")
          : t("academicSetup:no_grade_boundary_for_this_term_tooltip"),
        disabled: doesAllTermsHaveGradeBoundaries || isGradeBoundaryEmpty,
        visible: true,
      });
    }

    const options = [
      {
        key: "EDIT",
        label: t("common:edit"),
        disabled: !isGradingPeriodUnlocked,
        disabledTooltip: !isGradingPeriodUnlocked
          ? t(
              "academicSetup:grading_period_is_locked_grading_configurations_cannot_be_edited"
            )
          : null,
      },
      {
        isDivider: true,
      },
      ...resetToIBDefaultOption,
      ...duplicateOptions,
    ];

    return _.filter(options, ({ visible = true }) => visible);
  },
  params => JSON.stringify(params)
);

export const getUpdatedCategories = _.memoize(
  ({
    sourceGradeSet,
    targetGradeSet,
    weightageType,
    subjectLevelGenericTags,
    noSLorHL,
  }) => {
    const sourceFieldValues = _.get(sourceGradeSet, "fieldValues", []);
    const targetFieldValues = _.get(targetGradeSet, "fieldValues", []);

    const updatedFieldValues = _.map(targetFieldValues, targetValue => {
      const sourceValueToUpdateTargetValueWith = _.find(
        sourceFieldValues,
        sourceValue => sourceValue?.finalGrade === targetValue?.finalGrade
      );

      const updatedCategorieValues = [];
      const addedCategorieValues = [];
      const removedCategorieValues = [];

      if (noSLorHL) {
        const categoryValueId = _.get(
          _.find(targetValue, item => !_.isEmpty(item?.categoryValueId)),
          "categoryValueId"
        );
        const sourceValueItem = _.find(
          sourceValueToUpdateTargetValueWith,
          item => !_.isEmpty(item?.categoryValueId)
        );
        const categoryValueObj = {
          id: categoryValueId,
          genericTagId: null,
          valueType: "RANGE",
          weightageType,
          minValue: _.get(sourceValueItem, "minScore"),
          maxValue: _.get(sourceValueItem, "maxScore"),
        };
        updatedCategorieValues.push(categoryValueObj);
      }

      _.forEach(subjectLevelGenericTags, subjectLevelTag => {
        const { id: tagId, label: tagLabel } = subjectLevelTag;
        const sourceValueItem = _.find(
          sourceValueToUpdateTargetValueWith,
          item => _.get(item, "genericTag.id") === tagId
        );
        const targetValueItem = _.get(targetValue, [tagLabel]);
        const categoryValueId = _.get(targetValueItem, "categoryValueId");

        const categoryValueObj = {
          genericTagId: tagId,
          valueType: "RANGE",
          weightageType,
          minValue: _.get(sourceValueItem, "minScore"),
          maxValue: _.get(sourceValueItem, "maxScore"),
        };

        if (categoryValueId) {
          if (_.isEmpty(sourceValueItem)) {
            removedCategorieValues.push(categoryValueId);
          } else {
            updatedCategorieValues.push({
              id: categoryValueId,
              ...categoryValueObj,
            });
          }
        } else {
          addedCategorieValues.push(categoryValueObj);
        }
      });

      return {
        id: targetValue?.id,
        label: targetValue?.finalGrade,
        values: {
          updatedCategorieValues,
          addedCategorieValues,
          removedCategorieValues,
        },
      };
    });

    return {
      updatedCategories: updatedFieldValues,
    };
  },
  params => JSON.stringify(params)
);

export const getGradingPeriodDetails = _.memoize(
  ({ t, terms, grades, rootNodes }) => {
    let isGradingPeriodCreated = false;
    const subjects = _.filter(
      rootNodes,
      node => !_.includes(["OTHERS"], _.get(node, "type"))
    );

    const gradingPeriodOptions = _.map(terms, term => {
      const { id, label } = term;
      const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(term);

      const { termCategorySetsCount } = getTermWiseCategorySetsCount({
        rootNodes,
        termId: id,
      });
      isGradingPeriodCreated |= termCategorySetsCount > 0;
      const doesAllSubjectsHaveTemplates = getIsCategorySetsExistsForAllSubjects(
        {
          subjects,
          grades,
          targetTermId: id,
          isImportFromAnotherTerm: false,
        }
      );

      return {
        label,
        value: id,
        isDisabled: !isGradingPeriodUnlocked,
        disabledTooltip: t(
          "academicSetup:you_cannot_import_grade_boundaries_since_locked",
          { label }
        ),
        termCategorySetsCount,
        doesAllSubjectsHaveTemplates,
      };
    });

    return {
      gradingPeriodOptions,
      isGradingPeriodCreated,
    };
  },
  params => JSON.stringify(params)
);

export const getIBDefinedGradeBoundariesOptions = ({
  t,
  filteredIBDefinedData,
  selectedExamSession,
}) => {
  return _.map(filteredIBDefinedData, (item, index) => {
    const { timezone, month, year, url } = item;

    const label = `${t(
      `onBoarding:${_.toLower(month)}`
    )} examination session ${year}`;

    const key = `${label}-${index}`;
    const isChecked = key === selectedExamSession;

    return {
      icon: (
        <div>
          <RadioButton checked={isChecked} />
        </div>
      ),
      label: t("academicSetup:ib_defined_grade_boundary_for_label", {
        label: year,
      }),
      key,
      url,
      timezone,
      index,
    };
  });
};

export const getImportIBGradeBoundariesToastMsg = ({
  t,
  filteredGradingPeriods,
  isAllGradingPeriodsSelected,
}) => {
  if (_.isEmpty(filteredGradingPeriods)) {
    return {
      toastMessage: "academicSetup:no_blank_grade_boundaries_found",
    };
  } else if (_.size(filteredGradingPeriods) === 1) {
    const { label } = _.first(filteredGradingPeriods);
    return {
      toastMessage:
        "academicSetup:grade_boundaries_successfully_imported_for_label",
      localeParams: [{ key: "label", value: label, isPlainText: true }],
    };
  } else if (isAllGradingPeriodsSelected) {
    return {
      toastMessage:
        "academicSetup:grade_boundaries_successfully_imported_for_label_terms",
      localeParams: [
        { key: "label", value: t("common:all"), isPlainText: true },
      ],
    };
  }
  return {
    toastMessage:
      "academicSetup:grade_boundaries_successfully_imported_for_label_terms",
    localeParams: [
      {
        key: "label",
        value: _.toString(_.size(filteredGradingPeriods)),
        isPlainText: true,
      },
    ],
  };
};

export const GRADE_BOUNDARY_SUB_TYPES_INFO = {
  IB_DEFINED_TERM_GRADE_BOUNDARY: {
    labelLocale: "common:ib_defined",
    color: "violet",
  },
  CUSTOM_TERM_GRADE_BOUNDARY: {
    labelLocale: "common:custom",
    color: "yellow",
  },
};
